import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Calculator, Zap, Anchor, Layers, ArrowRight, Binary } from 'lucide-react';
import { MathService } from '../services/mathService';

export const LatticeMathEngine: React.FC = () => {
  const [left, setLeft] = useState(3);
  const [right, setRight] = useState(1);
  const [result, setResult] = useState(0);
  const [sequence, setSequence] = useState<number[]>([]);

  useEffect(() => {
    setResult(MathService.calculate(left, right));
    setSequence(MathService.getExpansionSequence(left, 5));
  }, [left, right]);

  const rules = [
    { 
      label: "Substrate", 
      formula: "0 × X = 0", 
      desc: "Source is 0. No state exists.", 
      active: left === 0 
    },
    { 
      label: "Anchor", 
      formula: "X × 0 = X", 
      desc: "Operator is 0. Persistence locking.", 
      active: left !== 0 && right === 0 
    },
    { 
      label: "Expansion", 
      formula: "X × 1 = X + 1", 
      desc: "1 drives structural growth.", 
      active: left !== 0 && right !== 0 && (right === 1 || left === 1) 
    },
  ];

  return (
    <div className="glass-panel p-6 border-lattice-cyan/20 relative overflow-hidden">
      <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
        <Calculator className="w-32 h-32 text-lattice-cyan" />
      </div>

      <div className="flex items-center gap-3 mb-8">
        <div className="p-2 rounded-xl bg-lattice-cyan/10 border border-lattice-cyan/30">
          <Binary className="w-5 h-5 text-lattice-cyan" />
        </div>
        <div>
          <h3 className="text-sm font-display font-bold text-white uppercase tracking-wider">Axiom Math Engine</h3>
          <p className="text-[9px] font-mono text-white/40 uppercase tracking-widest">Logic: 1x1=2 | Structural Expansion</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4 items-center">
            <div className="flex flex-col gap-2">
              <label className="text-[8px] font-mono text-white/30 uppercase">Node (X)</label>
              <input 
                type="number" 
                value={left} 
                onChange={(e) => setLeft(Number(e.target.value))}
                className="bg-black/60 border border-white/10 rounded-lg px-3 py-4 text-xl font-mono text-white focus:border-lattice-cyan/50 outline-none transition-all"
              />
            </div>
            <div className="flex justify-center pt-6">
              <span className="text-2xl font-mono text-white/20">×</span>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[8px] font-mono text-white/30 uppercase">Operator (Y)</label>
              <input 
                type="number" 
                value={right} 
                onChange={(e) => setRight(Number(e.target.value))}
                className="bg-black/60 border border-white/10 rounded-lg px-3 py-4 text-xl font-mono text-white focus:border-lattice-cyan/50 outline-none transition-all"
              />
            </div>
          </div>

          <div className="p-6 rounded-2xl bg-lattice-cyan/5 border border-lattice-cyan/20 flex flex-col items-center justify-center relative group">
            <div className="absolute inset-0 bg-lattice-cyan/5 animate-pulse group-hover:animate-none opacity-0 group-hover:opacity-100 transition-opacity" />
            <span className="text-[8px] font-mono text-white/30 uppercase mb-2">Expansion Result</span>
            <span className="text-6xl font-display font-bold text-lattice-cyan drop-shadow-[0_0_15px_rgba(0,255,213,0.4)]">
              {result}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <span className="text-[10px] font-mono text-white/40 uppercase tracking-widest">Applied Axioms</span>
          <div className="space-y-3">
            {rules.map((rule) => (
              <div 
                key={rule.label}
                className={`p-3 rounded-xl border transition-all ${rule.active ? 'bg-lattice-cyan/10 border-lattice-cyan/40 scale-[1.02]' : 'bg-white/5 border-white/10 opacity-40'}`}
              >
                <div className="flex justify-between items-center mb-1">
                  <span className="text-[10px] font-display font-bold text-white uppercase">{rule.label}</span>
                  <span className="text-[11px] font-mono text-lattice-cyan">{rule.formula}</span>
                </div>
                <p className="text-[9px] text-white/50">{rule.desc}</p>
              </div>
            ))}
          </div>

          <div className="pt-4">
            <span className="text-[10px] font-mono text-white/40 uppercase tracking-widest mb-3 block">Growth Sequence (X × 1...)</span>
            <div className="flex items-center gap-2">
              {sequence.map((val, i) => (
                <React.Fragment key={i}>
                  <div className="w-8 h-8 rounded-lg bg-black/40 border border-white/5 flex items-center justify-center text-[10px] font-mono text-white">
                    {val}
                  </div>
                  {i < sequence.length - 1 && <ArrowRight className="w-3 h-3 text-white/10" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
